import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";

import { validateBeforeFileUpload } from "../../utils/FileValidation";

import "../../styles/Resources.scss";

/**
 * EPR Single Student Resources
 *
 * - Allows file upload (FILE_UPLOAD) via EPR_GRADING_PROFILE_UPLOAD_FILE
 * - Allows URL resources (resourceType = "URL")
 * - Saves resources via EPR_GRADING_PROFILE_RESOURCES
 */
const Resources = ({
    canEdit,
    gradingProfileId,
    gradingProfileStudentId,
    initialResources,
    isActiveAndIsUnreleased,
    schoolYearKey,
    sectionId,
    studentNumber,
    studentOverallComment,
    token
}) => {
    const [resourceType, setResourceType] = useState("file");
    const [resources, setResources] = useState(initialResources || []);
    const [alias, setAlias] = useState("");
    const [uri, setUri] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [open, setOpen] = useState("false");
    const [isDeletingResource, setIsDeletingResource] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);
    const maxFileSize = 1000000000;

    useEffect(() => {
        setResources(initialResources || []);
    }, [initialResources]);

    const truncateAlias = (str) => {
        if (str && str.length > 50) {
            return `${str.substr(0, 50)}...`;
        }
        return str;
    };

    const resetFields = () => {
        setAlias("");
        setUri("");
        setSelectedFile(null);
        const fileInput = document.getElementById("student-resource-file-input");
        if (fileInput) fileInput.value = null;
    };

    const validateUrl = (value) => {
        if (!value) return false;
        try {
            const withScheme = value.startsWith("http") ? value : `http://${value}`;
            new URL(withScheme);
            return true;
        } catch (e) {
            return false;
        }
    };

    /**
     * Save resources array to backend using the "updateStudentResources" DAO action.
     * Body shape:
     * [
     *   {
     *     "key": gradingProfileStudentId,
     *     "gradingProfile": gradingProfileId,
     *     "studentNumber": ...,
     *     "sectionId": ...,
     *     "comment": null,
     *     "externalResources": [ ...resources ]
     *   }
     * ]
     */
    const saveResources = async (nextResources) => {
        if (!gradingProfileId || !gradingProfileStudentId) {
            console.error("Missing gradingProfileId or gradingProfileStudentId for resources");
            toast.error("There was a problem updating resources.", { autoClose: 5000 });
            return;
        }

        const dto = [
            {
                key: gradingProfileStudentId,
                gradingProfile: gradingProfileId,
                studentNumber,
                sectionId,
                comment: studentOverallComment,
                externalResources: nextResources
            }
        ];

        try {
            await ElementaryProgressReportDao({
                action: "updateStudentResources",
                gradingProfileId,
                data: dto,
                token
            });

            setResources(nextResources);

            refreshResourcesFromServer();
            toast.success("Resources successfully updated.", { autoClose: 3000 });
        } catch (err) {
            console.error(err);
            toast.error("There was a problem updating resources.", { autoClose: 5000 });
        }
    };

    /**
     * Upload file using DAO, then add to resources and save.
     */
    const handleUploadFile = async () => {
        const { valid, error, warnToast } = validateBeforeFileUpload(
            canEdit,
            selectedFile,
            alias,
            maxFileSize,
            resources
        );
        if (!valid && error && warnToast) {
            toast.warn(error, { autoClose: 4000 });
            return;
        } else if (!valid && error) {
            toast.error(error, { autoClose: 4000 });
            return;
        } else if (!valid) {
            return;
        }

        setIsUploading(true);

        const formData = new FormData();
        formData.append("file", selectedFile, selectedFile.name);

        try {
            const response = await ElementaryProgressReportDao({
                action: "uploadStudentResourceFile",
                studentNumber,
                schoolYearKey,
                data: formData,
                token
            });

            const payload = response?.data?.payload ?? response?.data;
            const uriFromServer = typeof payload === "string" ? payload : payload?.uri || null;
            const mimeTypeFromServer = payload?.mimeType || selectedFile.type || null;

            if (!uriFromServer) {
                throw new Error("Missing file URI in upload response");
            }

            const nextResources = [
                ...resources,
                {
                    uri: uriFromServer,
                    resourceType: "FILE_UPLOAD",
                    alias,
                    mimeType: mimeTypeFromServer
                }
            ];

            await saveResources(nextResources);
            resetFields();
            toast.success("File successfully uploaded and added to resources.", { autoClose: 3000 });
        } catch (err) {
            console.error(err);
            toast.error("There was a problem uploading the file.", {
                autoClose: false,
                closeOnClick: true
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleAddUrl = async () => {
        if (!canEdit) return;

        if (!uri || !alias) {
            toast.warn("Please enter a URL and description", { autoClose: 4000 });
            return;
        }

        if (!validateUrl(uri)) {
            toast.warn("The URL resource was blank or not formatted correctly", {
                autoClose: 5000
            });
            return;
        }

        const containsDuplicateAlias = resources.some((resource) => {
            return alias === resource.alias;
        });

        if (containsDuplicateAlias) {
            toast.error("A resource with that alias already exists. Please enter a unique alias. ");
            return;
        }

        const nextResources = [
            ...resources,
            {
                uri,
                resourceType: "URL",
                alias,
                mimeType: "text/plain"
            }
        ];

        await saveResources(nextResources);
        resetFields();
        toast.success("A URL has been added to resources", { autoClose: 3000 });
    };

    const handleRemoveResource = async () => {
        if (!canEdit) return;

        const nextResources = resources.filter((_, i) => i !== resourceToDelete.resources.index);

        try {
            const response = await ElementaryProgressReportDao({
                action: "deleteEprResource",
                resourceKey: resourceToDelete.resources.key,
                gradingProfileId,
                token
            });
            if (response) {
                setResources(nextResources);
                refreshResourcesFromServer();
                toast.success("Resource has been removed", { autoClose: 3000 });
            }
        } catch (err) {
            console.error("Error downloading resource file", err);
            toast.error("Unable to download file", { autoClose: 5000 });
        }
    };

    const downloadFile = async (resource) => {
        if (!resource?.key) {
            toast.info("This file is still being processed. Please try again in a moment.", {
                autoClose: 4000
            });
            return;
        }

        try {
            const response = await ElementaryProgressReportDao({
                action: "downloadStudentResourceFile",
                resourceKey: resource.key,
                token
            });

            const blob = response?.data;

            const mimeTypeFromResource = resource.mimeType || "application/pdf";
            const mimeTypeToUse = mimeTypeFromResource || "application/octet-stream";

            const fileBlob = new Blob([blob], { type: mimeTypeToUse });

            const url = window.URL.createObjectURL(fileBlob);

            const tab = window.open(url, "_blank", "noopener,noreferrer");
            if (tab) tab.opener = null;

            const link = document.createElement("a");
            link.href = url;

            const extension = mimeTypeToUse === "application/pdf" ? ".pdf" : "";

            link.download = (resource.alias || "download") + extension;
            link.style.display = "none";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000);
        } catch (err) {
            console.error("Error downloading resource file", err);
            toast.error("Unable to download file", { autoClose: 5000 });
        }
    };

    const refreshResourcesFromServer = async () => {
        try {
            const response = await ElementaryProgressReportDao({
                action: "getStudentResources",
                gradingProfileId,
                params: {
                    studentNumbers: String(studentNumber)
                },
                token
            });

            const payload = response?.data?.payload || [];
            const dtoForStudent = payload.find((dto) => String(dto.studentNumber) === String(studentNumber));

            if (dtoForStudent) {
                setResources(dtoForStudent.externalResources || []);
            }
        } catch (err) {
            console.error("Error refreshing resources from server", err);
        }
    };

    const openResource = (resource) => {
        if (resource.resourceType === "URL") {
            let newUrl = resource.uri || "";
            if (newUrl.search(/http/i) === -1) {
                newUrl = `http://${newUrl}`;
            }
            const win = window.open(newUrl, "_blank", "noopener,noreferrer");
            if (win) win.opener = null;
        } else if (resource.resourceType === "FILE_UPLOAD") {
            if (resource.uri && /^https?:/i.test(resource.uri)) {
                const win = window.open(resource.uri, "_blank", "noopener,noreferrer");
                if (win) win.opener = null;
            } else {
                downloadFile(resource);
            }
        }
    };

    const confirmDelete = (resources, index) => {
        setResourceToDelete({ resources, index });
        setOpen("confirm-remove-resource");
    };

    const handleDeleteDialogConfirm = async () => {
        if (resourceToDelete) {
            setIsDeletingResource(true);
            await handleRemoveResource();
            setResourceToDelete(null);
        }
        setOpen("false");
        setIsDeletingResource(false);
    };

    const handleDeleteDialogCancel = () => {
        setResourceToDelete(null);
        setOpen("false");
    };

    return (
        <>
            <section className="section-card resources-card epr-resources">
                <header className="section-card__header resources-card__header">
                    <div>
                        <h5 className="section-card__title">Supporting Resources</h5>
                        <p className="resources-card__subtitle">
                            Optionally include uploaded files and/or links that support the student&apos;s academic
                            progress report.
                        </p>
                    </div>
                    {canEdit && (
                        <div className="resources-card__actions">
                            <ActionButton
                                label="Add File"
                                className={
                                    resourceType === "file" || resourceType === ""
                                        ? "action-button-reg"
                                        : "ghost-button-reg"
                                }
                                onClick={() => {
                                    setResourceType("file");
                                    resetFields();
                                }}
                            />
                            <ActionButton
                                label="Add URL"
                                className={resourceType === "url" ? "action-button-reg" : "ghost-button-reg"}
                                onClick={() => {
                                    setResourceType("url");
                                    resetFields();
                                }}
                            />
                        </div>
                    )}
                </header>

                <div className="section-card__body resources-card__body">
                    {canEdit && (
                        <div className="resources-add-panel">
                            {resourceType === "file" && (
                                <>
                                    <div className="resources-add-panel__heading">
                                        <span className="resources-add-panel__title">File Upload</span>
                                        <span className="resources-add-panel__subtitle">
                                            &nbsp;- Choose a file, enter a description, then click&nbsp;
                                            <span className="resources-add-panel__title">Upload</span>
                                        </span>
                                    </div>

                                    <div className="input-container">
                                        <label
                                            htmlFor="student-resource-file-input"
                                            className="resources-field resources-field--file"
                                        >
                                            <span className="resources-field__label-text">File</span>
                                            <div className="browse-container">
                                                <input
                                                    id="student-resource-file-input"
                                                    aria-label="File Upload"
                                                    className="resources-input resources-input--file"
                                                    name="student-resource-file-input"
                                                    type="file"
                                                    disabled={!canEdit}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        setSelectedFile(file || null);
                                                        if (file) {
                                                            setAlias((prev) => file.name || prev);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </label>

                                        <label
                                            htmlFor="student-resource-description"
                                            className="resources-field resources-field--description"
                                        >
                                            <span className="resources-field__label-text">Description</span>
                                            <input
                                                id="student-resource-description"
                                                className="resources-input alias-input"
                                                disabled={!canEdit || !isActiveAndIsUnreleased}
                                                maxLength={255}
                                                name="alias"
                                                placeholder="Enter file description"
                                                type="text"
                                                value={alias}
                                                onChange={(e) => setAlias(e.target.value)}
                                            />
                                        </label>
                                    </div>

                                    <div className="resources-add-panel__hint">
                                        Max file size: {maxFileSize / 1000000}MB
                                    </div>

                                    <div className="resources-add-panel__footer">
                                        <div className="upload-info">
                                            {selectedFile && (
                                                <div className="upload-info__text">
                                                    Type: {selectedFile.type || "Unknown"}&nbsp;•&nbsp;Size:{" "}
                                                    {selectedFile.size} bytes
                                                </div>
                                            )}
                                        </div>
                                        <ActionButton
                                            disable={
                                                !canEdit ||
                                                isUploading ||
                                                selectedFile?.size > maxFileSize ||
                                                !selectedFile ||
                                                !alias ||
                                                !isActiveAndIsUnreleased
                                            }
                                            label={isUploading ? "Uploading..." : "Upload"}
                                            onClick={handleUploadFile}
                                        />
                                    </div>
                                </>
                            )}

                            {resourceType === "url" && (
                                <>
                                    <div className="resources-add-panel__heading">
                                        <span className="resources-add-panel__title">Add Link</span>
                                        <span className="resources-add-panel__subtitle">
                                            &nbsp;- Enter a URL, enter a description, then click&nbsp;
                                            <span className="resources-add-panel__title">Add Resource</span>.
                                        </span>
                                    </div>

                                    <div className="input-container">
                                        <label
                                            htmlFor="student-resource-url-input"
                                            className="resources-field resources-field--url"
                                        >
                                            <span className="resources-field__label-text">URL</span>
                                            <div className="uri-container">
                                                <input
                                                    className="uri-description"
                                                    id="student-resource-url-input"
                                                    name="uri"
                                                    disabled={!canEdit || !isActiveAndIsUnreleased}
                                                    placeholder="Enter URL"
                                                    type="text"
                                                    value={uri}
                                                    onChange={(e) => setUri(e.target.value)}
                                                />
                                            </div>
                                        </label>

                                        <label
                                            htmlFor="student-resource-uri-description"
                                            className="resources-field resources-field--description"
                                        >
                                            <span className="resources-field__label-text">Description</span>
                                            <div className="uri-description-container">
                                                <input
                                                    name="alias"
                                                    id="student-resource-uri-description"
                                                    className="resources-input uri-description"
                                                    disabled={!canEdit || !isActiveAndIsUnreleased}
                                                    placeholder="Enter description"
                                                    type="text"
                                                    value={alias}
                                                    onChange={(e) => setAlias(e.target.value)}
                                                />
                                            </div>
                                        </label>
                                    </div>

                                    <div className="resources-add-panel__actions">
                                        <ActionButton
                                            label="Add Resource"
                                            onClick={handleAddUrl}
                                            disable={!uri || !alias || !isActiveAndIsUnreleased}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="resources-list">
                        <div className="resources-list__header">
                            <h6 className="resources-list__title">Associated Resources</h6>
                            {resources && resources.length > 0 && (
                                <span className="resources-list__count">
                                    {resources.length} item{resources.length === 1 ? "" : "s"}
                                </span>
                            )}
                        </div>

                        {resources && resources.length > 0 ? (
                            <div className="resources-list__items">
                                {resources.map((resource, index) => {
                                    const isUrl = resource.resourceType === "URL";
                                    const isFile = resource.resourceType === "FILE_UPLOAD";

                                    return (
                                        <section className="resource-line-item-container" key={`resource-${index}`}>
                                            <div className="resource-resource">
                                                <div className="alias-container">
                                                    <div className="alias-content">
                                                        <div className="resource-main">
                                                            <div
                                                                role="button"
                                                                tabIndex={0}
                                                                className="resource-link"
                                                                onClick={() => openResource(resource)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" || e.key === " ") {
                                                                        openResource(resource);
                                                                    }
                                                                }}
                                                            >
                                                                {isFile && (
                                                                    <>
                                                                        <i className="bi bi-paperclip" />
                                                                        <span>{truncateAlias(resource.alias)}</span>
                                                                    </>
                                                                )}

                                                                {isUrl && (
                                                                    <>
                                                                        <i className="bi bi-link-45deg" />
                                                                        <span>{truncateAlias(resource.alias)}</span>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {isFile && (
                                                                <i
                                                                    className="bi bi-download resource-download-icon"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    title="Open file"
                                                                    onClick={() => downloadFile(resource)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            downloadFile(resource);
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </div>

                                                        <div className="resource-type-label">
                                                            <em>{isFile ? "File" : "Link"}</em>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="remove-button">
                                                    {canEdit && isActiveAndIsUnreleased && (
                                                        <ActionButton
                                                            className="action-button-80"
                                                            label="Remove"
                                                            onClick={() => confirmDelete(resource, index)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </section>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="resources-list__empty">
                                <span className="resources-list__empty-text">
                                    There are no resources associated with this student yet.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <DcsdDialog
                actions={
                    <>
                        <ActionButton
                            ariaLabel="Cancel"
                            className="action-button-reg"
                            label="Cancel"
                            onClick={() => handleDeleteDialogCancel()}
                        ></ActionButton>
                        <ActionButton
                            ariaLabel="Remove Resource"
                            className="action-button-delete"
                            label={isDeletingResource ? "Removing..." : "Remove Resource"}
                            disable={isDeletingResource ? true : false}
                            onClick={() => handleDeleteDialogConfirm()}
                        ></ActionButton>
                    </>
                }
                ariaLabel="Confirm Remove Resource"
                hasCloseX={false}
                id="confirm-remove-resource"
                open={open}
                title="Delete Resource Confirmation"
                data-testid="delete-modal"
            >
                <div>
                    <span className="bold-label">
                        Resource Name:{" "}
                        {resourceToDelete?.resources?.alias ?? resourceToDelete?.resources?.uri ?? "Unknown"}
                    </span>
                    Please confirm you would like to remove this resource. Otherwise, click Cancel.
                </div>
            </DcsdDialog>
        </>
    );
};

Resources.propTypes = {
    canEdit: PropTypes.bool.isRequired,
    gradingProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gradingProfileStudentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    initialResources: PropTypes.arrayOf(
        PropTypes.shape({
            uri: PropTypes.string,
            resourceType: PropTypes.string,
            alias: PropTypes.string,
            mimeType: PropTypes.string,
            creatorDisplayName: PropTypes.string
        })
    ),
    isActiveAndIsUnreleased: PropTypes.bool,
    schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    studentNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    studentOverallComment: PropTypes.string,
    token: PropTypes.string.isRequired
};

Resources.defaultProps = {
    gradingProfileId: null,
    initialResources: [],
    schoolYearKey: null,
    sectionId: null,
    studentOverallComment: null
};

export default Resources;
